import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardActionArea,
  Typography,
  Grid,
  LinearProgress,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip
} from '@mui/material';
import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { brandColors } from '../../theme/theme';
import { exportElementAsImage } from '../../lib/utils/exportAsImage';
import { formatProjectionDate } from '../../types/boxOfficeTypes';

const clampPercentage = (value = 0) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 0;
  }
  return Math.min(Math.max(value, 0), 100);
};

const defaultLogoSrc = `${process.env.PUBLIC_URL || ''}/images/logo.svg`;
const defaultPosterPlaceholder = `${process.env.PUBLIC_URL || ''}/images/poster-placeholder.svg`;

const isNumeric = (value) => typeof value === 'number' && Number.isFinite(value);

const formatCompactCurrency = (amount) => {
  if (!isNumeric(amount)) {
    return 'N/A';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 1
  }).format(amount);
};

const ProjectionRangeBar = ({ summary }) => {
  if (!summary) return null;

  const {
    actualProjection,
    projectionFloor,
    projectionCeiling,
    outlierMinimum,
    outlierCeiling,
    projectionDate,
    updatedAt
  } = summary;

  const floorValue = isNumeric(projectionFloor)
    ? projectionFloor
    : isNumeric(outlierMinimum)
    ? outlierMinimum
    : isNumeric(actualProjection)
    ? actualProjection
    : null;

  const likelyValue = isNumeric(actualProjection)
    ? actualProjection
    : isNumeric(projectionFloor) && isNumeric(projectionCeiling)
    ? (projectionFloor + projectionCeiling) / 2
    : null;

  const ceilingValue = isNumeric(projectionCeiling)
    ? projectionCeiling
    : isNumeric(outlierCeiling)
    ? outlierCeiling
    : isNumeric(actualProjection)
    ? actualProjection
    : null;

  if (floorValue === null && likelyValue === null && ceilingValue === null) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: 1.5,
        p: 1.25,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: '#f7f9fe',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 700, color: brandColors.primary, letterSpacing: 0.4 }}>
        Opening Weekend Projection
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 1
        }}
      >
        <Box
          sx={{
            p: 1.25,
            borderRadius: 1.5,
            backgroundColor: 'rgba(198,40,40,0.12)',
            border: '1px solid rgba(198,40,40,0.25)'
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#c62828', letterSpacing: 0.3 }}>
            Floor
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#c62828' }}>
            {formatCompactCurrency(floorValue)}
          </Typography>
        </Box>

        <Box
          sx={{
            p: 1.25,
            borderRadius: 1.5,
            backgroundColor: 'rgba(255,202,40,0.18)',
            border: '1px solid rgba(255,202,40,0.35)'
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#f9a825', letterSpacing: 0.3 }}>
            Projection
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#f9a825' }}>
            {formatCompactCurrency(likelyValue)}
          </Typography>
        </Box>

        <Box
          sx={{
            p: 1.25,
            borderRadius: 1.5,
            backgroundColor: 'rgba(102,187,106,0.12)',
            border: '1px solid rgba(102,187,106,0.25)'
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#2e7d32', letterSpacing: 0.3 }}>
            Ceiling
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2e7d32' }}>
            {formatCompactCurrency(ceilingValue)}
          </Typography>
        </Box>
      </Box>

      {updatedAt && (
        <Typography variant="caption" color="text.secondary">
          Updated {formatProjectionDate(updatedAt)}
        </Typography>
      )}
    </Box>
  );
};

const FilmTile = ({
  film,
  metrics,
  footerLines,
  onClick,
  enableExport = true,
  exportFileName,
  logoSrc = defaultLogoSrc,
  posterSrc,
  posterAlt,
  posterFallback = defaultPosterPlaceholder,
  sx = {},
  allowNavigation = true,
  showSurveyDateInHeader = true,
  renderMetrics,
  projectionSummary
}) => {
  const tileRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const resolvedPoster = posterSrc || film?.poster_url || film?.primary_image_url || posterFallback;

  const handleExportImage = async (event) => {
    event.stopPropagation();

    if (!tileRef.current || exporting) {
      return;
    }

    try {
      setExporting(true);
      const fileBase =
        exportFileName ||
        `${(film?.imdb_title || film?.film_title || 'film')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '')}-tile`;

      await exportElementAsImage(tileRef.current, {
        fileName: `${fileBase}.png`,
        onStart: () => setExporting(true),
        onComplete: () => setExporting(false)
      });
    } catch (error) {
      // Swallow error — already logged inside export utility
      setExporting(false);
    }
  };

  const headerInfo = useMemo(() => {
    const title = film?.imdb_title || film?.film_title || 'Untitled Film';
    const releaseDate = film?.release_date_formatted || film?.release_date_display || film?.release_date;
    const surveyDate = film?.survey_period || film?.survey_date;

    return {
      title,
      releaseDate,
      surveyDate
    };
  }, [film]);

  const content = (
    <Box
      ref={tileRef}
      sx={{
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: '#ffffff',
        gap: 2,
        ...sx
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          component="img"
          src={resolvedPoster}
          alt={posterAlt || `${headerInfo.title} poster`}
          onError={(e) => {
            e.currentTarget.src = posterFallback;
          }}
          crossOrigin="anonymous"
          loading="lazy"
          sx={{
            width: 72,
            height: 108,
            borderRadius: 1,
            objectFit: 'cover',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            backgroundColor: '#f5f5f5',
            flexShrink: 0
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: brandColors.primary,
              lineHeight: 1.2,
              mb: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical'
            }}
          >
            {headerInfo.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            {headerInfo.releaseDate ? `Release: ${headerInfo.releaseDate}` : 'Release date pending'}
          </Typography>
          {showSurveyDateInHeader && headerInfo.surveyDate && (
            <Typography variant="caption" color="text.secondary">
              Survey completed {headerInfo.surveyDate}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Metrics */}
      {renderMetrics
        ? renderMetrics({ metrics, clampPercentage, projectionSummary })
        : metrics?.length > 0 && (
            <Grid container spacing={1.5}>
              {metrics.map((metric) => {
                const value = clampPercentage(metric.value);
                const color = metric.color || brandColors.primary;
                const displayValue =
                  metric.formatValue?.(metric.value) ??
                  `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}${metric.suffix ?? '%'}`;

                return (
                  <Grid item xs={12} sm={6} key={metric.key || metric.title}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor: `${color}30`,
                        backgroundColor: `${color}08`,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.75
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{ color }}>{metric.icon}</Box>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                          {metric.title}
                        </Typography>
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, color, display: 'flex', alignItems: 'center', gap: 0.75 }}
                      >
                        {displayValue}
                        {metric.change !== undefined && metric.change !== null && (
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              color:
                                metric.change > 0
                                  ? brandColors.highInterest
                                  : metric.change < 0
                                  ? '#f44336'
                                  : 'text.secondary'
                            }}
                          >
                            {metric.change > 0 ? '+' : ''}
                            {metric.change.toFixed(1)}%
                          </Typography>
                        )}
                      </Typography>
                      {metric.subtitle && (
                        <Typography variant="caption" color="text.secondary">
                          {metric.subtitle}
                        </Typography>
                      )}
                      {metric.showProgress !== false && (
                        <LinearProgress
                          variant="determinate"
                          value={value}
                          sx={{
                            height: 4,
                            borderRadius: 2,
                            backgroundColor: `${color}20`,
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: color,
                              borderRadius: 2
                            }
                          }}
                        />
                      )}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}

      {/* Box Office Projection */}
      {projectionSummary && <ProjectionRangeBar summary={projectionSummary} />}

      {/* Footer */}
      {footerLines?.length > 0 && (
        <Box
          sx={{
            mt: 'auto',
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {footerLines.map((line, index) => (
              <Typography key={index} variant="caption" color="text.secondary">
                {line}
              </Typography>
            ))}
          </Box>
          {logoSrc && (
            <Box
              component="img"
              src={logoSrc}
              alt="Greenlight Analytics"
              sx={{ height: 28, width: 'auto', flexShrink: 0 }}
            />
          )}
        </Box>
      )}
    </Box>
  );

  return (
    <Card
      sx={{
        position: 'relative',
        height: '100%',
        borderRadius: 2,
        border: `2px solid ${film?.dataSourceColor || brandColors.primary}20`,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: allowNavigation && onClick ? 'translateY(-2px)' : 'none',
          boxShadow: allowNavigation && onClick ? '0 12px 32px rgba(0,0,0,0.12)' : undefined
        }
      }}
    >
      {enableExport && (
        <Tooltip title={exporting ? 'Preparing image…' : 'Download tile image'}>
          <span>
            <IconButton
              onClick={handleExportImage}
              disabled={exporting}
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 2,
                backgroundColor: '#ffffff',
                '&:hover': { backgroundColor: '#f5f5f5' },
                boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
              }}
            >
              {exporting ? <CircularProgress size={18} thickness={5} /> : <FileDownloadIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      )}

      {allowNavigation && onClick ? (
        <CardActionArea onClick={onClick} sx={{ height: '100%' }}>
          {content}
        </CardActionArea>
      ) : (
        content
      )}
    </Card>
  );
};

FilmTile.propTypes = {
  film: PropTypes.object.isRequired,
  metrics: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string.isRequired,
      value: PropTypes.number,
      change: PropTypes.number,
      subtitle: PropTypes.string,
      icon: PropTypes.node,
      color: PropTypes.string,
      showProgress: PropTypes.bool,
      suffix: PropTypes.string,
      formatValue: PropTypes.func
    })
  ),
  footerLines: PropTypes.arrayOf(PropTypes.string),
  onClick: PropTypes.func,
  enableExport: PropTypes.bool,
  exportFileName: PropTypes.string,
  logoSrc: PropTypes.string,
  posterSrc: PropTypes.string,
  posterAlt: PropTypes.string,
  posterFallback: PropTypes.string,
  sx: PropTypes.object,
  allowNavigation: PropTypes.bool,
  showSurveyDateInHeader: PropTypes.bool,
  renderMetrics: PropTypes.func,
  projectionSummary: PropTypes.shape({
    actualProjection: PropTypes.number,
    projectionFloor: PropTypes.number,
    projectionCeiling: PropTypes.number,
    outlierMinimum: PropTypes.number,
    outlierCeiling: PropTypes.number,
    projectionDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    updatedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    confidence: PropTypes.string,
    notes: PropTypes.string
  })
};

FilmTile.defaultProps = {
  metrics: [],
  footerLines: [],
  enableExport: true,
  allowNavigation: true,
  showSurveyDateInHeader: true,
  projectionSummary: null
};

export default FilmTile;

